<template>
    <div
        id="createAccount"
        class="create-account"
        :class="{
            'hide-social': hideSocialOptions,
            'parental-flow': parentalFlowMode,
        }"
    >
        <FormError data-cy="page-error" :data-cy-data="errorCode" :hide-error="!errorCode" :error-code="errorCode" />

        <accountContainer :class="{ narrow: parentalFlowMode }" :sub-title-text="$t(title)">
            <column class="pull-left">
                <parental-signup-guide v-if="parentalFlowMode" :mode="parentalFlowMode" />

                <form class="input-container">
                    <regionSelect />

                    <template v-if="!isVerifiedAge">
                        <h5 id="page-main-heading">{{ $t(verifyAgeTitle) }}</h5>
                        <CountrySelector
                            v-if="!isSteamConnector()"
                            id="country-select"
                            v-model="$v.inputCountryCode.$model"
                            string-selector="_common.country-list"
                            :has-error="submitted && $v.inputCountryCode.$error"
                            :disabled="parentalFlowMode === 'child'"
                            :order-by-loco="!isUnified"
                            @input="updateCountry"
                        />

                        <countrySelectFaq v-if="!isSteamConnector()" data-cy="country-select-faq"></countrySelectFaq>

                        <FormDOB
                            v-model="$v.inputDateOfBirth.$model"
                            data-cy="date-of-birth"
                            :has-error="submitted && $v.inputDateOfBirth.$error"
                            :disabled="isUnified && !inputCountryCode && !isSteamConnector()"
                        />
                        <!-- visible v2 -->
                        <reCaptcha
                            v-if="showCaptcha"
                            v-model="$v.reCaptchaCode.$model"
                            :has-error="$v.reCaptchaCode.$error"
                            :override-error-text="$t('error.form.complete-captcha')"
                        />

                        <FormButton
                            class="btn-gradient"
                            data-cy="continue"
                            type="button"
                            :button-click="submitDOB"
                            :loading="buttonLoading"
                            :label="$t('create-account.complete.continue')"
                        />
                    </template>
                    <template v-else>
                        <h5 id="page-main-heading">{{ $t(subTitle) }}</h5>
                        <FormInput
                            id="email"
                            v-model="$v.inputEmail.$model"
                            data-cy="email"
                            type="email"
                            :has-error="submitted && $v.inputEmail.$error"
                            :override-error-text="$t(inputEmailErrorText, [3, 50])"
                            :enter-handler="createAccountHandler"
                            :placeholder="$t('create-account.email')"
                        />
                        <FormPassword
                            id="password"
                            v-model="newPassword"
                            data-cy="password"
                            type="password"
                            :has-error="!!newPasswordErrorMessage && !showPasswordStrength"
                            :override-error-text="newPasswordErrorMessage"
                            :enter-handler="createAccountHandler"
                            :placeholder="$t('login.password')"
                            @focus="showPasswordStrength = true"
                            @blur="showPasswordStrength = false"
                        />
                        <PasswordStrength
                            :visible="showPasswordStrength"
                            :password="newPassword"
                            :email="inputEmail"
                            :submitted="submitted"
                            :is-signup="true"
                            @setErrorMessage="(value) => (newPasswordErrorMessage = value)"
                        />
                        <FormInput
                            id="fullname"
                            v-model="$v.inputFullName.$model"
                            data-cy="full-name"
                            type="text"
                            :has-error="submitted && ($v.inputFullName.$error || inputFullNameError)"
                            :override-error-text="
                                inputFullNameError ? '' : $t(inputNameErrorText, [minNameLength, maxNameLength])
                            "
                            :enter-handler="createAccountHandler"
                            :placeholder="$t('create-account.full-name')"
                        />

                        <FormCheckbox
                            v-if="parentalFlowMode === 'child'"
                            v-model="createChildAccountAgreement"
                            :label-text="$t('parental-controls.child.signup.agreement')"
                        />

                        <template v-if="parentalFlowMode !== 'child'">
                            <FormCheckbox
                                id="newsletter-opt-in"
                                v-model="inputNewsLetterOptIn"
                                data-cy="newsletter"
                                name="news"
                                class="large"
                                :label-text="$t('create-account.newsletter-opt-in')"
                            />
                            <FormCheckbox
                                v-if="isLauncherMode()"
                                id="auto-login"
                                class="large"
                                :checked="autologin"
                                name="auto-login"
                                :label-text="$t('login.auto-login')"
                                @change="(val) => $store.commit('login/launcher/setAutologin', val)"
                            />
                            <termsLinksAccountCreation
                                v-model="$v.touAgree.$model"
                                name="agreeTou"
                                :has-error="submitted && $v.touAgree.$error"
                            />
                        </template>

                        <FormButton
                            id="submitCreateAccount"
                            class="btn-gradient"
                            data-cy="submit"
                            type="button"
                            :button-click="createAccountHandler"
                            :loading="buttonLoading"
                            :label="$t(submitTitle)"
                            :disabled="createChildAgreeCheck"
                        />
                    </template>
                </form>

                <!-- invisible v2 -->
                <vue-recaptcha
                    v-if="recaptcha.loaded"
                    ref="recaptcha"
                    :sitekey="recaptchaSiteKey"
                    size="invisible"
                    @verify="recaptchaV2Verified"
                    @expired="resetRecaptcha"
                />

                <recaptcha-attribution />

                <ul class="help list-unstyled">
                    <li>
                        <router-link id="login-link" :to="{ name: 'login' }">
                            <svgIcon icon-holder-class="icon-help" icon-id="icon-page-right" />
                            {{ $t('create-account.help.have-account') }}
                        </router-link>
                    </li>
                </ul>
            </column>

            <column v-if="!hideSocialOptions" class="pull-right">
                <div class="divider">or</div>
                <thirdParty
                    v-if="!hasFromOPENAPI_WEBSITE"
                    tpa-type="accountCreationTPA"
                    :has-twitter-login="false"
                    :third-party-header="$t('create-account.third-party-header')"
                    :oauth-state="{
                        ...(parentalFlowMode
                            ? {
                                  parental_flow_mode: parentalFlowMode,
                                  route_from: $route.name,
                              }
                            : {}),
                    }"
                >
                </thirdParty>

                <recaptcha-attribution />
                <div class="clearfix" />
            </column>

            <div class="clearfix"></div>

            <div v-if="productId" class="product-logo" :class="`product-${productId}`"></div>
        </accountContainer>
        <svgIcons class="hide"></svgIcons>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { FormButton, FormCheckbox, FormError, FormInput, FormPassword } from '@arena/toolkit';
import svgIcons from '@arena/toolkit/src/components/common/icon-sprite';
import { requiredIf, required, minLength, maxLength, sameAs, email, helpers } from 'vuelidate/lib/validators';
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';
import { getBlackbox } from '@arena/toolkit/src/plugins/iovation';
import { waitForCookieSet } from '@/plugins/helpers';
import VueRecaptcha from 'vue-recaptcha';
import accountContainer from '@/components/account-container';
import column from '@/components/column-container';
import recaptchaAttribution from '@/components/recaptcha-attribution';
import regionSelect from '@/components/region-select';
import thirdParty from '@/components/third-party';
import termsLinksAccountCreation from '@/components/terms-links-account-creation';
import countrySelectFaq from '@/components/country-select-faq';
import launcherAPI from '@/modules/launcher';
import downloadLauncher from '@/modules/launcher/download';
import recaptchaKeys from '@/config/recaptcha-key';
import recaptchaV3Wrapper from '@/plugins/recaptcha-v3-wrapper';
import CountrySelector from '@/components/country-selector';
import PasswordStrength from '@/components/password-strength';
import FormDOB from '@/components/global/form-dob';
import parentalSignupGuide from '@/components/parental-controls/signup-guide.vue';
import reCaptcha from '@/components/recaptcha';

const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 64;
const NAME_ERROR_ID = 'error.input.full-name';

export default {
    name: 'CreateAccount',
    components: {
        accountContainer,
        FormInput,
        FormPassword,
        countrySelectFaq,
        FormCheckbox,
        FormButton,
        FormError,
        FormDOB,
        thirdParty,
        column,
        svgIcon,
        termsLinksAccountCreation,
        regionSelect,
        VueRecaptcha,
        recaptchaAttribution,
        PasswordStrength,
        svgIcons,
        CountrySelector,
        parentalSignupGuide,
        reCaptcha,
    },
    metaInfo() {
        return {
            titleTemplate: `%s | ${this.$t('create-account.create-nexon-account')}`,
        };
    },
    props: {
        isUnified: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            inputCountryCode: null,
            inputEmail: '',
            newPassword: '',
            inputFullName: '',
            inputFullNameError: false,
            inputDateOfBirth: '',
            inputNewsLetterOptIn: false,
            errorCode: 0,
            buttonLoading: false,
            submitData: {},
            showCountrySelectError: false,
            showDateError: false,
            submitted: false,
            touAgree: null,
            recaptcha: {
                loaded: false,
                action: {
                    web: 'web_create_account',
                    launcher: 'launcher_create_account',
                },
                token: '',
                version: '',
            },
            minNameLength: MIN_NAME_LENGTH,
            maxNameLength: MAX_NAME_LENGTH,
            showPasswordStrength: false,
            newPasswordErrorMessage: '',
            isVerifiedAge: false,
            showCaptcha: false,
            reCaptchaCode: '',
            selectedCountry: null,
            createChildAccountAgreement: false,
        };
    },
    validations() {
        return {
            inputCountryCode: {
                requiredUnlessSteamConnector: (value) => helpers.req(value) || launcherAPI.isSteamConnector(),
            },
            inputEmail: {
                required,
                email,
                maxLength: maxLength(50),
            },
            newPassword: {
                required,
            },
            inputFullName: {
                required,
                minLength: minLength(MIN_NAME_LENGTH),
                maxLength: maxLength(MAX_NAME_LENGTH),
            },
            inputDateOfBirth: { required },
            touAgree: { sameAs: sameAs(() => true) },
            reCaptchaCode: { required: requiredIf(() => this.showCaptcha) },
        };
    },
    computed: {
        ...mapState('login/launcher', ['autologin']),
        ...mapState('country', ['countryCodes', 'ipMembershipNo']),
        hasFromOPENAPI_WEBSITE() {
            if (typeof window.sessionStorage !== 'undefined') {
                return !!window.sessionStorage.getItem('from_openapi_website');
            }
            return false;
        },
        signupParams() {
            return this.$store.state.signupParams;
        },
        productId() {
            return this.$cookie.get('ProdId') || '';
        },
        thirdPartyList() {
            return this.$store.state.thirdPartyList;
        },

        inputEmailErrorText() {
            if (!this.$v.inputEmail.required) {
                return 'error.input.email';
            }
            if (!this.$v.inputEmail.maxLength) {
                return 'error.input.length.id';
            }
            return 'error.input.not-valid-email';
        },
        inputNameErrorText() {
            return `${NAME_ERROR_ID}${
                !this.$v.inputFullName.minLength || !this.$v.inputFullName.maxLength ? '-length' : ''
            }`;
        },
        recaptchaSiteKey() {
            return recaptchaKeys.v2;
        },
        language() {
            return this.$i18n.locale;
        },
        hideSocialOptions() {
            return environment.subRegion === 'ru';
        },
        getCurrentTime() {
            return Date.now();
        },
        abTestType() {
            return this.$route.query.signup_type;
        },
        parentalFlowMode() {
            switch (this.$route.name) {
                case 'add-child-signup':
                    return 'child';
                case 'create-parent-account':
                    return 'parent';
                default:
                    return undefined;
            }
        },
        title() {
            if (this.parentalFlowMode === 'parent') return 'parental-controls.parent.signup.title';
            if (this.parentalFlowMode === 'child') return 'parental-controls.child.signup.title';
            return '';
        },
        verifyAgeTitle() {
            if (this.parentalFlowMode === 'child') return 'parental-controls.child.signup.verify-age-title';
            return 'create-account.verify-age-title';
        },
        subTitle() {
            if (this.parentalFlowMode === 'parent') return 'parental-controls.parent.signup.sub-title';
            if (this.parentalFlowMode === 'child') return 'parental-controls.child.signup.sub-title';
            return 'create-account.create-nexon-account';
        },
        submitTitle() {
            if (this.parentalFlowMode === 'child') return 'parental-controls.child.signup.submit';
            return 'create-account.create-account';
        },
        isJapan() {
            return environment.region === 'japan';
        },
        createChildAgreeCheck() {
            return this.parentalFlowMode === 'child' && !this.createChildAccountAgreement;
        },
    },
    watch: {
        errorCode(val) {
            if (val) window.scrollTo(0, 0); // for top error bar's visibility
        },
        inputFullName() {
            this.inputFullNameError = false;
        },
    },
    mounted() {
        if (this.isUnified && !this.$route.query.unified) {
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    unified: 'unified',
                },
            });
        }

        if (this.isTWRegion()) {
            this.inputNewsLetterOptIn = true;
        }

        recaptchaV3Wrapper.isRecaptchaLoaded(this).then(() => {
            this.recaptcha.loaded = true;
        });

        if (this.$route.params.accountId) this.inputEmail = this.$route.params.accountId;
        if (this.signupParams) {
            this.inputEmail = this.signupParams.id;
            this.inputFullName = this.signupParams.name;
            this.inputNewsLetterOptIn = this.signupParams.newsletter;
            this.inputCountryCode = this.signupParams.countryCode;
            this.$regionalAPI
                .delete(`no-auth/${this.isUnified ? 'unified/' : ''}signup`, {
                    data: { id: this.signupParams.id, device_id: this.$store.getters.deviceId },
                })
                .finally(() => {
                    this.$store.commit('setSignupParams', null);
                });
        }
        if (this.$route.params.errorObj) {
            this.createAccountFailure(this.$route.params.errorObj);
        }

        if (this.parentalFlowMode !== this.$route.query.parental_flow_mode) {
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    ...(this.parentalFlowMode ? { parental_flow_mode: this.parentalFlowMode } : {}),
                },
            });
            return;
        }
    },
    async created() {
        // inherit parent's country code
        if (this.parentalFlowMode === 'child') {
            await this.$store.dispatch('settings/refreshAccountData');
            this.inputCountryCode = this.$store.state.settings.countryCode;
        }
        // for nxLog Funner tracking backend need a data when page initiated
        await waitForCookieSet('arenaSid');
        this.$store.dispatch('create/init', { isUnified: this.isUnified });
    },
    methods: {
        ...mapActions('login', ['login']),
        ...mapActions('country', ['getRegionInfo']),
        isLauncherMode: launcherAPI.isAppMode,
        isSteamConnector: launcherAPI.isSteamConnector,
        setBirthdate(value) {
            this.inputDateOfBirth = value;
            this.showDateError = false;
        },
        setFieldValidationStatus(field, isValidated) {
            this.validateFields[field] = !isValidated;
        },
        allFieldsValid() {
            return Object.values(this.validateFields).every((x) => Boolean(x));
        },
        resetRecaptcha() {
            if (this.$refs.recaptcha) {
                this.$refs.recaptcha.reset();
                this.setRecaptchaTokenValues('', '');
            }
        },
        // invisible v2 captcha
        recaptchaV2Verified(token) {
            console.log('recaptchaV2Verified');
            this.setRecaptchaTokenValues(token, 'v2');
            this.createAccountRequester();
        },
        async submitDOB() {
            this.$v.$touch();
            this.submitted = true;
            this.$v.inputCountryCode.$touch();
            this.$v.inputDateOfBirth.$touch();
            if (this.$v.inputCountryCode.$invalid || this.$v.inputDateOfBirth.$invalid) return;

            this.buttonLoading = true;
            try {
                const { isMinorAge } = await this.$store.dispatch('create/checkAge', {
                    countryCode: this.inputCountryCode,
                    dob: this.inputDateOfBirth,
                });

                dataLayer.push({
                    event: 'DOBSubmit',
                    Underage: isMinorAge ? 'true' : 'false',
                    fromTPA: 'false',
                });

                if (isMinorAge && this.isJapan) {
                    this.errorCode = 'coppa';
                    this.buttonLoading = false;
                    return;
                } else if (isMinorAge && this.parentalFlowMode !== 'child') {
                    const params = this.isUnified ? { membershipNo: this.selectedCountry.membershipNo } : {};
                    this.$router.push({
                        name:
                            this.parentalFlowMode === 'parent'
                                ? 'parental-controls.not-eligible'
                                : 'parent-consent-required',
                        params,
                    });
                    return;
                } else if (!isMinorAge && this.parentalFlowMode === 'child') {
                    this.errorCode = 'DOB_INVALID_FOR_CHILD';
                } else {
                    this.errorCode = 0;
                    this.submitted = false;
                    this.isVerifiedAge = true;
                    setTimeout(() => {
                        const elInputEmail = document.querySelector('input#email');
                        if (elInputEmail) elInputEmail.focus();
                    });
                }

                this.showCaptcha = false; // once visible v2 captcha is used, v3 needs to be used for next as Euisok's request
            } catch ({ errorCode }) {
                if (errorCode === 20072) {
                    this.showCaptcha = true;
                    this.errorCode = errorCode;
                } else {
                    this.showCaptcha = false;
                    this.createAccountFailure({ errorCode });
                }
            }

            this.buttonLoading = false;
        },
        createAccountHandler() {
            if (this.parentalFlowMode === 'child') {
                this.touAgree = true; // child flow doesn't require tou agreement
            }

            this.$v.$touch();
            this.submitted = true;

            setTimeout(() => {
                console.log('this.$v.$invalid', this.$v.$invalid);
                if (!this.$v.$invalid && !this.newPasswordErrorMessage) {
                    this.createAccountRequester();
                }
                if (this.$v.$invalid) {
                    this.reportValidationErrors();
                }
            }, 10);
        },
        reportValidationErrors() {
            if (this.$v.inputCountryCode.$invalid) {
                this.pushAccCreationDataLayer('Country Code', 'Field Error');
            }
            if (this.$v.inputDateOfBirth.$invalid) {
                this.pushAccCreationDataLayer('DOB', 'Field Error');
            }
            if (this.$v.inputEmail.$invalid) {
                this.pushAccCreationDataLayer('Email', 'Field Error');
            }
            if (this.$v.inputFullName.$invalid) {
                this.pushAccCreationDataLayer('Name', 'Field Error');
            }
            if (this.$v.touAgree.$invalid) {
                this.pushAccCreationDataLayer('TOU', 'Field Error');
            }
        },
        pushAccCreationDataLayer(field, message) {
            dataLayer.push({
                event: 'SignupLoginErrorView',
                errorName: `${field} - ${message}`,
            });
        },
        async createAccountRequester() {
            async function waitForBlackbox(retriesLeft = 10, interval = 300) {
                const { blackbox, finished } = await getBlackbox();
                if (finished) {
                    return blackbox;
                } else if (retriesLeft) {
                    await new Promise((r) => setTimeout(r, interval));
                    return waitForBlackbox(retriesLeft - 1, interval);
                } else return blackbox;
            }
            if (this.recaptcha.version !== 'v2') {
                const token = await recaptchaV3Wrapper.getRecaptchaV3Token(
                    this,
                    this.isLauncherMode() ? this.recaptcha.action.launcher : this.recaptcha.action.web
                );
                this.setRecaptchaTokenValues(token, 'v3');
            }
            this.resetBeforeSend();
            return waitForBlackbox()
                .then((blackbox) => {
                    this.getSignupParams(blackbox);
                    return this.$store.dispatch('create/signUp', this.signupParams);
                })
                .then(this.createAccountSuccess)
                .catch(this.createAccountFailure)
                .finally(this.afterCreationResponse);
        },
        getSignupParams(blackbox) {
            const signupParams = {
                id: this.inputEmail,
                name: this.inputFullName,
                password: this.newPassword,
                birthday: this.inputDateOfBirth,
                newsletter: this.inputNewsLetterOptIn,
                blackbox,
                countryCode: this.inputCountryCode,
                verificationTag: this.$store.state.channel === 'toy' ? `app_type=${this.$store.state.channel}` : '',
                captchaToken: this.recaptcha.token,
                captchaVersion: this.recaptcha.version,
                deviceId: this.$store.getters.deviceId,
            };
            this.$store.commit('setSignupParams', signupParams);
        },
        createAccountSuccess(data) {
            dataLayer.push({
                event: 'accountCreated',
                userId: data.hashedUserNo,
                globalUserId: data.hashedGlobalUserNo,
                'Account Type': 'Nexon',
                'Email Opt-In': this.inputNewsLetterOptIn ? 'True' : 'False',
            });
            if (this.parentalFlowMode === 'child') {
                this.$router.replace({ name: 'add-child-complete' });
            } else {
                this.$router.push({
                    name: 'login-verify-email',
                });
            }
        },
        afterCreationResponse() {
            this.buttonLoading = false;
        },
        setRecaptchaTokenValues(token, version) {
            this.recaptcha.token = token;
            this.recaptcha.version = version;
        },
        resetBeforeSend() {
            this.buttonLoading = true; // button spinner behavior
            this.errorCode = 0; // reset error
        },
        requestFail({ errorCode = 'unknown' }) {
            this.errorCode = errorCode;
            this.pushAccCreationDataLayer('Submit', errorCode);
            this.resetRecaptcha();
            this.buttonLoading = false;
            this.submitted = true;
        },
        createAccountFailure({ errorCode }) {
            this.$store.commit('setSignupParams', null);
            try {
                // ALREADY_EXIST_ID_IN_OTHER_MEMBERSHIP
                if (errorCode === 20011) {
                    this.$accountAPI.get(`/no-auth/id/membership/${this.inputEmail}`).then(async (data) => {
                        const regionRedirectURL = data.accountSettingUrl;
                        if (this.isLauncherMode()) {
                            let regionCode = /([a-z]*)\.nexon\.com/.exec(regionRedirectURL)[1];
                            if (regionCode.toLowerCase() === 'www') {
                                regionCode = 'global';
                            }
                            const regionTitle = `Nexon ${this.$t(`regions.${regionCode}`)}`;

                            await this.openModal(
                                `${errorCode}_launcher`,
                                () => {
                                    downloadLauncher(regionCode);
                                },
                                { regionTitle }
                            );
                        } else {
                            await this.openModal(errorCode, () => {
                                window.location.href = regionRedirectURL;
                            });
                        }
                    });
                    // .catch(this.createAccountFailure);
                }
                if (errorCode >= 20031 && errorCode <= 20034) {
                    this.inputFullNameError = true;
                }
                if (errorCode === 20072) {
                    this.buttonLoading = false;
                    dataLayer.push({ event: 'createAccountCaptcha' });
                    console.log('recaptchaV2 reload');
                    this.$refs.recaptcha.execute();
                    return;
                }
                this.errorCode = errorCode;
                this.pushAccCreationDataLayer('Submit', errorCode);
            } catch (ex) {
                this.pushAccCreationDataLayer('Submit', 'Unknown - createAccountFailure');
                this.errorCode = 'unknown';
            }
            this.resetRecaptcha();
            this.buttonLoading = false;
            this.submitted = true;
        },
        async openModal(error, okFunc, i18nData = {}) {
            return this.$dialog.confirm({
                title: `error.modal.${error}.title`,
                content: `error.modal.${error}.desc`,
                confirmLabel: `error.modal.${error}.button`,
                confirmFunction: okFunc,
                cancelLabel: 'cancel',
                i18nData,
            });
        },
        updateCountry() {
            if (!this.isUnified) return;
            const prev = this.selectedCountry;
            this.selectedCountry = this.countryCodes.find((x) => x.code === this.inputCountryCode);
            if (!prev || prev.membershipNo !== this.selectedCountry.membershipNo) {
                this.$store.dispatch('create/init', {
                    isUnified: this.isUnified,
                    membershipNo: this.selectedCountry.membershipNo,
                });
            }
        },
    },
};
</script>

<style lang="less">
.hide-social {
    .contents {
        max-width: 400px;
    }
    .column {
        min-width: 320px;
        max-width: 400px;

        &.pull-left,
        &.pull-right {
            max-width: 400px;
        }
    }

    .pull-left {
        float: none !important;
        margin: 0 auto;
    }
}

.pull-left {
    .recaptcha-attribution {
        display: block;
    }
}
.pull-right {
    .divider {
        display: none;
        flex-direction: row;
        color: white;
        &:before,
        &:after {
            content: '';
            flex: 1 1;
            border-bottom: 1px solid #4c4c4c;
            margin: auto;
        }

        &:before {
            margin-right: 10px;
        }
        &:after {
            margin-left: 10px;
        }
    }
    .recaptcha-attribution {
        display: none;
    }
}

.parental-flow {
    .contents,
    .pull-left,
    .pull-right {
        max-width: 400px !important;
        width: 100%;
    }

    .pull-left {
        .recaptcha-attribution {
            display: none;
        }
    }
    .pull-right {
        .divider {
            display: flex;
        }
        .recaptcha-attribution {
            display: block;
        }
    }
}

@media (max-width: 740px) {
    .pull-left {
        .recaptcha-attribution {
            display: none;
        }
    }
    .pull-right {
        .divider {
            display: flex;
        }
        .recaptcha-attribution {
            display: block;
        }
    }
}
.dob-wrapper {
    .form-group {
        margin-bottom: 0;
    }
}
</style>
